import React from 'react'
import { useTranslation } from 'react-i18next'
import BackArrowSvg from '../other/svg/BackArrowSvg'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  totalItems: number
  itemsPerPage?: number
}

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage = 20
}: PaginationProps): JSX.Element => {
  const { t } = useTranslation()

  if (totalPages === 0) {
    return <div />
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  const renderPageNumbers = (): JSX.Element[] => {
    const pages = []
    const maxVisiblePages = 10

    pages.push(
      <button
        key={1}
        onClick={() => {
          onPageChange(1)
        }}
        className={`px-4 py-2 mx-1 rounded-2 ${
          currentPage === 1 ? 'bg-groupBlue text-white' : 'bg-white text-groupBlue hover:bg-lightGrey'
        }`}>
        1
      </button>
    )

    const startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3)

    if (startPage > 2) {
      pages.push(
        <span key="start-ellipsis" className="mx-2 text-darkGrey">
          ...
        </span>
      )
    }

    for (let i = startPage; i <= endPage; i += 1) {
      pages.push(
        <button
          key={i}
          onClick={() => {
            onPageChange(i)
          }}
          className={`px-4 py-2 mx-1 rounded-2 ${
            currentPage === i ? 'bg-groupBlue text-white' : 'bg-white text-groupBlue hover:bg-lightGrey'
          }`}>
          {i}
        </button>
      )
    }

    if (endPage < totalPages - 1) {
      pages.push(
        <span key="end-ellipsis" className="mx-2 text-darkGrey">
          ...
        </span>
      )
    }

    if (totalPages > 1) {
      pages.push(
        <button
          key={totalPages}
          onClick={() => {
            onPageChange(totalPages)
          }}
          className={`px-4 py-2 mx-1 rounded-2 ${
            currentPage === totalPages ? 'bg-groupBlue text-white' : 'bg-white text-groupBlue hover:bg-lightGrey'
          }`}>
          {totalPages}
        </button>
      )
    }

    return pages
  }

  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="text-sm text-darkGrey mb-3">
        {t('pagination.showing_items', { startItem, endItem, totalItems })}
      </div>
      <div className="flex items-center justify-center space-x-2">
        <button
          onClick={() => {
            onPageChange(currentPage - 1)
          }}
          disabled={currentPage === 1}
          className={`p-2 rounded-2 ${
            currentPage === 1
              ? 'bg-lightGrey text-grey cursor-not-allowed'
              : 'bg-white text-groupBlue hover:bg-lightGrey'
          }`}>
          <BackArrowSvg className="w-6 h-6" />
        </button>

        {renderPageNumbers()}

        <button
          onClick={() => {
            onPageChange(currentPage + 1)
          }}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-2 ${
            currentPage === totalPages
              ? 'bg-lightGrey text-grey cursor-not-allowed'
              : 'bg-white text-groupBlue hover:bg-lightGrey'
          }`}>
          <BackArrowSvg className="w-6 h-6 rotate-180" />
        </button>
      </div>
    </div>
  )
}

export default Pagination
